import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Habit } from '@/types'
import { queueSync, getCurrentUserId, generateUUID } from '@/lib/sync'

interface HabitState {
  habits: Habit[]

  // Actions
  addHabit: (habit: Omit<Habit, 'id' | 'userId' | 'currentStreak' | 'bestStreak' | 'createdAt' | 'completedToday'>) => void
  removeHabit: (id: string) => void
  completeHabit: (id: string) => { xpReward: number; streakBonus: number } | null
  getHabit: (id: string) => Habit | undefined
  resetDailyCompletions: () => void
}

const generateId = generateUUID

export const useHabitStore = create<HabitState>()(
  persist(
    (set, get) => ({
      habits: [],

      addHabit: (habit) => {
        const newHabit: Habit = {
          id: generateId(),
          userId: getCurrentUserId(),
          title: habit.title,
          icon: habit.icon,
          xpReward: habit.xpReward,
          currentStreak: 0,
          bestStreak: 0,
          createdAt: new Date().toISOString(),
          completedToday: false,
        }

        set((state) => ({
          habits: [...state.habits, newHabit],
        }))

        // Queue for Supabase sync
        queueSync('habits', 'create', newHabit.id, {
          user_id: newHabit.userId,
          title: newHabit.title,
          icon: newHabit.icon,
          xp_reward: newHabit.xpReward,
          current_streak: newHabit.currentStreak,
          best_streak: newHabit.bestStreak,
          created_at: newHabit.createdAt,
        })
      },

      removeHabit: (id) => {
        set((state) => ({
          habits: state.habits.filter((h) => h.id !== id),
        }))

        // Queue for Supabase sync
        queueSync('habits', 'delete', id, {})
      },

      completeHabit: (id) => {
        const state = get()
        const habit = state.habits.find((h) => h.id === id)

        if (!habit || habit.completedToday) return null

        const newStreak = habit.currentStreak + 1
        const streakBonus = Math.floor(newStreak / 7) * 10 // +10 XP per week of streak
        const newBestStreak = Math.max(habit.bestStreak, newStreak)

        set((state) => ({
          habits: state.habits.map((h) =>
            h.id === id
              ? {
                  ...h,
                  currentStreak: newStreak,
                  bestStreak: newBestStreak,
                  completedToday: true,
                }
              : h
          ),
        }))

        // Queue for Supabase sync
        queueSync('habits', 'update', id, {
          current_streak: newStreak,
          best_streak: newBestStreak,
        })

        return {
          xpReward: habit.xpReward,
          streakBonus,
        }
      },

      getHabit: (id) => get().habits.find((h) => h.id === id),

      resetDailyCompletions: () => {
        set((state) => ({
          habits: state.habits.map((h) => {
            // If not completed today, break the streak
            if (!h.completedToday) {
              return { ...h, currentStreak: 0, completedToday: false }
            }
            // Reset completedToday for next day
            return { ...h, completedToday: false }
          }),
        }))
      },
    }),
    {
      name: 'waifudo-habits',
    }
  )
)
